import { Link, usePage } from "@inertiajs/react"
import { MoreHorizontal } from "lucide-react"
import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import AppLogoIcon from "@/components/app-logo-icon"
// OKS-402 — Badge-earned modal (subscribes to `pending_badge` shared prop).
import { BadgeEarnModal } from "@/components/badge/badge-earn-modal"
// OKS-701 — brand-context strip, mounted shell-globally under the header
// on every creator page (generalizes the OKS-385 missions-home pattern).
// Collapses to a read-only current-brand display for single-crew creators.
import { BrandStrip } from "@/components/brand-strip"
// OKS-555 — "your moment" cheer modal (subscribes to `pending_cheer` shared prop).
import { YourMomentModal } from "@/components/cheer/your-moment-modal"
// OKS-60 — Coin balance pill in the creator-shell header (left of XpPill).
import { CoinsPill } from "@/components/coins/coins-pill"
// OKS-376 — App-wide "Connect TikTok" optional-connect banner.
import { ConnectTiktokBanner } from "@/components/creator/connect-tiktok-banner"
import { CreatorActivityHeaderTrigger } from "@/components/creator/creator-activity-header-trigger"
import { HeaderNavUser } from "@/components/header-nav-user"
import { CreatorMessagesHeaderTrigger } from "@/components/messaging/creator-messages-header-trigger"
import { BellTrigger } from "@/components/notifications/bell-trigger"
// OKS-668 — mobile-only, one-time "Add to Home Screen" install tutorial
// (self-gating bottom Drawer; renders null unless creator + mobile + not
// standalone + not seen).
import { AddToHomeScreenDrawer } from "@/components/pwa/add-to-home-screen-drawer"
// OKS-397 — Level-up modal (subscribes to `pending_rank_up` shared prop;
// replaces the OKS-49 Sonner toast).
import { RankUpModal } from "@/components/rank/rank-up-modal"
// OKS-385 — bottom-tab nav extracted into a config-driven shared component
// (TRD KD-1/KD-2). The creator 5-slot config + behavior are unchanged.
import { TabBar, buildCreatorTabs } from "@/components/tab-bar"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
// OKS-48 — XP balance pill in the creator-shell header.
import { XpPill } from "@/components/xp/xp-pill"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useMediaQuery } from "@/hooks/use-media-query"
import { usePollUnreadCount } from "@/hooks/use-poll-unread-count"
import { useRealtimeRefetch } from "@/hooks/use-realtime-refetch"
import { useUnreadMessagesCount } from "@/hooks/use-unread-messages-count"
// OKS-739 — shared 480px app-column (centers content/nav/CTA at `lg+`).
import { CREATOR_APP_COLUMN } from "@/lib/creator-layout"
import { cn } from "@/lib/utils"
import { creatorMissionsPath } from "@/routes"

/**
 * CreatorShell — mobile-first shell for `creator` role.
 *
 * Layout:
 *   - <1024px: top bar (logo · CrewSwitcher · NavUser) + bottom-tab nav.
 *   - ≥1024px: top bar only (desktop creator rail is future work).
 *
 * The bottom-tab nav (Missions / Rewards / Crew / Learn* / Profile; 4 or 5
 * slots) is rendered by the shared `<TabBar>` (OKS-385 extraction); the
 * creator config lives in `buildCreatorTabs()`. Missions is the home tab;
 * Rewards is slot 2 (OKS-864). Learn is gated on `hasMasterclassContent`
 * (the active brand's published masterclasses); when absent the nav is 4
 * slots.
 */
export function CreatorShell({ children }: PropsWithChildren) {
  // OKS-36 — creator-side bell polling. UX Spec §2 puts the bell in
  // the top header (not the bottom-tab nav).
  usePollUnreadCount()
  // OKS-406 — real-time push. Subscribes to `CreatorChannel`; on a
  // refetch signal, partial-reloads the bell count / rank-up / badge
  // shared props for a ~sub-second live update. Silently degrades to the
  // poll above when the socket is unavailable (no connection-status UI).
  useRealtimeRefetch()
  // OKS-387 — responsive header (UX Spec §10). At ≥sm (640px) Messages +
  // Bell render inline; below sm they fold into a single "More" popover
  // to keep the header within a 320px viewport. `useMediaQuery` (sync
  // `useSyncExternalStore`) gives a single rendered instance per branch
  // — no duplicate testids (a CSS hidden/show would double them).
  const isSmUp = useMediaQuery("(min-width: 640px)")
  // OKS-701 — the crew switcher no longer lives in the header. It now
  // renders shell-globally in the below-navbar `<BrandStrip>` on EVERY
  // creator page, so the OKS-385 per-surface header-suppression (missions
  // home only) is gone — there is no header switcher left to suppress.
  // OKS-484 (A) — "N to submit" badge count for the Missions bottom-tab slot.
  // Creator-only + brand-scoped shared prop; 0 → no badge.
  // OKS-570 — `hasMasterclassContent` gates the Learn slot (creator-only,
  // brand-scoped; true iff the active brand has ≥1 published masterclass).
  // OKS-701 — `currentBrand` gates the below-navbar strip: no brand (a
  // creator with zero crews) → the strip row is omitted entirely (the
  // page owns its own empty state), matching `<BrandStrip>`'s own guard.
  // OKS-716 — `availableBrands` (server-filtered to the creator's OWN
  // memberships) drives the single- vs multi-crew home reconciliation below.
  const {
    toSubmitCount,
    hasMasterclassContent,
    currentBrand,
    availableBrands,
  } = useCurrentContext()
  // OKS-723 — the creator Messages surface is a FIXED-VIEWPORT chat: its thread
  // scrolls INTERNALLY while the header + composer stay put, so the shell must
  // bound the column to the viewport rather than let it grow. Every other
  // creator page is a normal body-scroll document. We branch on the active
  // Inertia page component (stable, path-based — see `entrypoints/inertia.tsx`
  // resolve; the CI trace proved this detection works in the container) so ONLY
  // Messages gets the bounded shell; the other 21 pages are untouched.
  const pageComponent = usePage().component
  const isFixedViewportSurface = pageComponent === "Creator/Messages/Index"
  // OKS-716 — on the creator HOME the brand identity now lives ON the cover
  // (bottom-left logo + name overlay in `<BrandCoverBand>`). For a SINGLE-crew
  // creator there is nothing to switch, so the shell's read-only identity strip
  // would be a duplicate name directly above the cover hero → suppress the whole
  // strip row on home only. MULTI-crew creators KEEP the strip (its
  // `<CrewSwitcher>` is a functional control, not a duplicate — CTPO steer).
  //
  // Blast radius is fenced to exactly (home ∧ single-crew): every NON-home
  // creator page and every MULTI-crew creator renders the strip unchanged. This
  // intentionally revises the merged OKS-717 invariant (strip present on home
  // for single-crew) — the strip is now present on home only for multi-crew.
  const isCreatorHome = pageComponent === "Creator/Missions"
  const isSingleCrew = availableBrands.length <= 1
  const suppressBrandStripOnHome = isCreatorHome && isSingleCrew
  return (
    // OKS-723 — for the Messages route the shell root is a `flex-1 min-h-0`
    // sibling of the app-banner-stack inside a `h-[100dvh] overflow-hidden` flex
    // column (`app-layout.tsx`). Flexbox hands it EXACTLY `100dvh − banner-stack`
    // with NO measured var — the app banner takes its natural height (`shrink-0`,
    // whatever it wraps to: 117px, 197px, 0…) and the shell fills the rest. This
    // is the fix for the `--banner-stack-h` ResizeObserver SETTLE RACE that made
    // the old measured-var calc() height render full-height until the observer
    // published (composer below the fold on CI-Linux, where the banner wraps to
    // 197px). No var, no calc, no observer → correct on first paint,
    // deterministic, banner-height-agnostic.
    //   - Messages: `flex-1 min-h-0` — bounded by the wrapper; the internal
    //     `flex-1 min-h-0` chain lets the thread scroll instead of growing.
    //   - Every other creator page: `min-h-screen` — grows with content = body
    //     scroll, byte-identical to pre-OKS-723.
    <div
      className={cn(
        "bg-background flex w-full flex-col",
        isFixedViewportSurface ? "min-h-0 flex-1" : "min-h-screen",
      )}
    >
      <header
        className="border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-1 border-b px-3 backdrop-blur sm:gap-2 sm:px-4"
        data-testid="creator-shell-header"
      >
        <Link
          href={creatorMissionsPath()}
          aria-label="OKS"
          // OKS-266 — drop the square `size-8` wrapper; the canonical
          // OKS lockup is 3.13:1 (NOT square). The link is height-
          // constrained (h-8) and width-fits the lockup at h-5 (~63px
          // wide). `text-sidebar-primary` keeps the orange brand color
          // on the creator-shell's light header surface.
          //
          // OKS-387 — hidden below `sm` (UX Spec §10, UX-Q7 CTPO-approved):
          // the ~63px lockup is what makes the multi-brand header fit at
          // 320px. Home stays reachable via the bottom-tab nav, so no
          // function is lost.
          className="text-sidebar-primary hidden h-8 shrink-0 items-center justify-center rounded-md sm:inline-flex"
        >
          <AppLogoIcon className="h-5 text-current" />
        </Link>
        <div className="flex-1" />
        {/* OKS-60 — Coin balance pill, immediately LEFT of XpPill
            (UX Spec §2). Same lambda-form shared-prop posture as XP;
            amber token to differentiate at a glance. */}
        <CoinsPill />
        {/* OKS-48 — XP balance pill. Lambda-form shared prop drives
            the count; the pill self-handles loading + creator-only
            rendering, so we drop it in unconditionally on the creator
            shell. */}
        <XpPill />
        {/* OKS-387 — Messages + Bell inline at ≥sm; folded into the
            "More" popover below sm (UX Spec §10). */}
        {isSmUp ? (
          <>
            {/* OKS-548 — Activity feed entry (recognition surface). Distinct
                from the bell popover; both live in the creator shell only. */}
            <CreatorActivityHeaderTrigger />
            <CreatorMessagesHeaderTrigger />
            <BellTrigger />
          </>
        ) : (
          <CreatorHeaderMoreMenu />
        )}
        <HeaderNavUser />
      </header>

      {/* OKS-799 — `<UnverifiedEmailBanner />` used to mount HERE, as the
          first row under the sticky header (OKS-146 introduced it between
          header + main; OKS-717 promoted it above the brand strip). CTPO
          reversed the placement: it was an account-hygiene prompt taxing
          100% of creator pageviews to serve a one-time task. It now renders
          as page content on `/profile` and once in the shared Settings
          frame. Do NOT re-add it here — the shell is back to
          header → brand strip → content (UX Spec §7), with nothing between
          the header and the strip. The OKS-146 gate is untouched and still
          surfaces contextually via `<CollabGateAlert>` at action time. */}

      {/* OKS-701 — full-width brand-context row beneath the sticky header on
          every creator page (UX Spec §2). Multi-crew creators get the switch
          affordance; single-crew creators get a read-only current-brand
          display — both handled inside `<BrandStrip>`. Gated on `currentBrand`
          so a zero-crew creator renders no row (the page owns its empty
          state). The wrapper carries the full-bleed bottom border + horizontal
          padding; `<BrandStrip>`'s own border/padding are dropped here so the
          separator spans edge-to-edge.

          OKS-717 → OKS-799 — the unverified-email banner briefly preceded this
          row; it was relocated to Profile + Settings, so the strip is once again
          the FIRST row directly beneath the sticky header. Its own markup,
          gating, and full-bleed treatment never changed through either move.

          OKS-723 — a `shrink-0` flex-column sibling (like the header + banners):
          it takes its natural height and the Messages `<main>` (`flex-1`)
          absorbs the rest, so no chrome measurement is needed.

          OKS-716 — additionally suppressed on the creator HOME for single-crew
          creators (`suppressBrandStripOnHome`): the cover now carries the brand
          identity there, so the read-only strip would be a duplicate name. The
          entire wrapper is render-gated (not visually hidden) so QA can assert
          its ABSENCE on `/missions` for single-crew and its PRESENCE everywhere
          else. Multi-crew home keeps the strip (CrewSwitcher is a control). */}
      {currentBrand && !suppressBrandStripOnHome && (
        <div
          data-testid="creator-shell-brand-strip"
          className="border-border/60 shrink-0 border-b px-3 py-3 sm:px-4"
        >
          <BrandStrip className="border-b-0 pb-0" />
        </div>
      )}

      {/* OKS-376 — App-wide "Connect TikTok" banner. Returns null unless
            the relaxation flag is ON, the creator is unconnected, AND the
            banner hasn't been dismissed this session. Account-level (TikTok
            is per-user), so it persists across brand switches until the
            creator connects. */}
      <ConnectTiktokBanner />

      {/* OKS-702 — reserve the TabBar's TRUE total height (4rem row +
          env(safe-area-inset-bottom)) so the last list item clears the taller
          bar in standalone PWA. In-browser the inset is 0 → 5rem, matching the
          previous `pb-20`.

          OKS-739 — the TabBar now persists at ALL widths (was `lg:hidden`), so
          the reserved bottom padding must too: the `lg:pb-0` override is GONE
          (nav visibility + reserved space can never diverge — PRD Core Rule),
          keeping `pb-[calc(5rem+env(safe-area-inset-bottom))]` at every width.
          On wide screens (`lg+`) the content column centers to the shared
          `CREATOR_APP_COLUMN` (data-testid `creator-app-column`) so the app
          reads as an intentional centered column, not a stretched phone. Inert
          below `lg` → phone + small-iPad portrait unchanged.

          OKS-723 — `flex flex-1 flex-col min-h-0`: `main` soaks up the height the
          header + strip + banners leave and is itself a flex column, so a
          full-height page (Creator/Messages) opts in with `flex-1 min-h-0` and
          its thread scrolls internally. `min-h-0` here is LOAD-BEARING for that:
          a flex item defaults to `min-height:auto` and refuses to shrink below
          its content, so without it the tall thread propagates up through
          `main` and defeats the bounded root (composer ends up far below the
          fold). It is inert for the 21 body-scroll pages — the root there is
          `min-h-` (grows with content), so nothing forces `main` to shrink;
          `flex-grow` still fills and content still overflows into the growing
          root = same body scroll as before. The `lg+` max-width caps the cross
          axis only; it does not touch the vertical flex chain. */}
      <main
        data-testid="creator-app-column"
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          // OKS-945 lever B — on the fixed-viewport Messages route ONLY, the
          // tab-bar reserve drops from 5rem to 4rem, returning 16px to the
          // message band. `components/tab-bar.tsx` is
          // `h-[calc(4rem+env(safe-area-inset-bottom))]`, so both terms carry
          // the SAME `env()` and the delta is exactly 16px at every inset
          // value, standalone PWA included.
          //
          // PROVENANCE — cite this rather than reconstructing intent. The 16px
          // is not OKS-702 breathing room: the comment above records that
          // OKS-702 added the `env()` TERM and that "in-browser the inset is 0
          // → 5rem, matching the previous `pb-20`". The 5rem was inherited
          // from a pre-existing `pb-20` and carried forward unchanged;
          // `git log -S 'calc(5rem'` returns only 285285f5 (OKS-702, added
          // `+env()`) and 6873458d (OKS-739, removed the `lg:pb-0` override).
          // There is no stated rationale for the 16px anywhere, so reducing it
          // disturbs nothing either ticket deliberately established.
          //
          // 🔴 OKS-739's PRD Core Rule binds here: nav visibility and reserved
          // space can never diverge. The TabBar is still visible on this
          // route, so the reserve must still fully clear it — this is safe
          // ONLY because `4rem + env()` is exactly the bar's true total
          // height. Reserve equals bar, zero occlusion. Any value below that
          // violates the rule.
          //
          // 🔴 Do NOT respell this as the bare spacing-scale utility for 4rem
          // (the one with no `calc()` and no `env()` term). The two are
          // identical in-browser at inset 0 and identical in a diff review,
          // but that spelling silently drops the safe-area term and re-opens
          // exactly the standalone-PWA occlusion OKS-702 existed to fix. It is
          // the one way this lever ships a regression that looks correct.
          // (That utility is deliberately not written out even as an example
          // here: Tailwind scans comments, so naming it would emit it into the
          // bundle and destroy the diff grep that guards against it.)
          //
          // ⚠️ This leaves the composer FLUSH with the tab bar, and
          // `messaging-creator-composer-viewport.spec.ts` asserts
          // `composer.y + composer.height <= nav.y + 1` — flush passes on that
          // 1px tolerance with sub-pixel rounding as the entire margin. If it
          // reds, the ruled fix is to reserve `calc(4rem+1px+env(...))`, which
          // costs 1px of the 16. NEVER widen the tolerance: that assertion IS
          // the occlusion guard, and loosening it is the `toBeVisible()`
          // failure mode this ticket exists to repair.
          isFixedViewportSurface
            ? "pb-[calc(4rem+env(safe-area-inset-bottom))]"
            : "pb-[calc(5rem+env(safe-area-inset-bottom))]",
          CREATOR_APP_COLUMN,
        )}
        tabIndex={-1}
      >
        {children}
      </main>

      {/* OKS-397 — Level-up modal (UX Spec). Mounts here so every
          creator-shell render has the shared-prop pickup path; renders
          nothing until a non-null `pending_rank_up` arrives, then opens
          a centered celebration the creator must actively dismiss. */}
      <RankUpModal />

      {/* OKS-402 — Badge-earned modal. Same shared-prop pickup path;
          renders nothing until a non-null `pending_badge` arrives, then
          opens a centered celebration the creator actively dismisses. */}
      <BadgeEarnModal />

      {/* OKS-555 — "your moment" cheer modal. Same shared-prop pickup path;
          renders nothing until a non-null `pending_cheer` arrives (live via
          `useRealtimeRefetch`), then opens the celebration + Cheer-back. */}
      <YourMomentModal />

      {/* OKS-668 — mobile-only "Add to Home Screen" tutorial. Creator-only by
          virtue of mounting in this shell; self-gates on mobile viewport +
          not-standalone + not-seen, so it renders no DOM for most traffic. */}
      <AddToHomeScreenDrawer />

      <TabBar
        items={buildCreatorTabs(toSubmitCount, hasMasterclassContent)}
        labelNamespace="creator_nav"
        navTestId="creator-bottom-nav"
        itemTestIdPrefix="creator-tab"
      />
    </div>
  )
}

/**
 * CreatorHeaderMoreMenu — OKS-387 (UX Spec §10).
 *
 * Below `sm` the header can't fit Messages + Bell as standalone icons
 * within a 320px viewport, so they fold into a single 44px "More"
 * ellipsis button that opens a Popover. Both triggers are reused
 * VERBATIM inside the panel — `CreatorMessagesHeaderTrigger` (a `/messages`
 * link) and `BellTrigger` (its own Sheet/Popover) keep their existing
 * behavior; we only relocate them.
 *
 * A combined unread dot on the More button reflects unread messages OR
 * notifications, so the signal isn't lost when the icons are collapsed
 * (the per-trigger badges still render inside the panel). The button
 * is `size-11` (44px) per the touch-target floor.
 *
 * Rendered only below `sm` (the parent gates on `useMediaQuery`), so it
 * never duplicates the inline ≥sm triggers.
 */
function CreatorHeaderMoreMenu() {
  const { t } = useTranslation()
  const { unreadNotificationsCount } = useCurrentContext()
  const unreadMessages = useUnreadMessagesCount()
  const hasUnread = unreadMessages > 0 || unreadNotificationsCount > 0

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground relative size-11"
            data-testid="creator-header-more-trigger"
            aria-label={t("creator_nav.more")}
          >
            <MoreHorizontal aria-hidden="true" className="size-5" />
            {hasUnread && (
              <span
                aria-hidden="true"
                data-testid="creator-header-more-unread-dot"
                className="bg-destructive absolute top-1 right-1 size-2 rounded-full"
              />
            )}
          </Button>
        }
      />
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-48 p-1"
        data-testid="creator-header-more-popover"
      >
        {/* OKS-598 — each item is ONE full-width interactive control (icon +
            label together), so the whole row is the tap target — not just the
            icon. Activity/Messages are navigation Links; the Notifications row
            is itself the bell panel trigger (opens in place, no nav, no nested
            button). See TRD §4. */}
        <div className="flex flex-col">
          <CreatorActivityHeaderTrigger variant="row" />
          <CreatorMessagesHeaderTrigger variant="row" />
          <BellTrigger variant="row" />
        </div>
      </PopoverContent>
    </Popover>
  )
}
